/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode, useCallback, useEffect} from 'react';
import {makeStyles} from '@material-ui/core';
import classnames from 'classnames';
import Tree from 'rc-tree';
import {AssetVehicleIcon} from '../icons/AssetVehicleIcon';
import {MaterialExpandLessIcon} from '../icons/MaterialExpandLessIcon';
import {MaterialExpandMoreIcon} from '../icons/MaterialExpandMoreIcon';
import {ScDeviceIcon} from '../icons/ScDeviceIcon';
import {dlsColor, dlsSvgColorize} from '../theme/theme';
import {CachedData} from '../types/types';
import './treeStyles.scss';
import 'rc-tree/assets/index.css';

interface TreeDisplayProps {
  nodes: ReactNode[];
  className?: string;
  autoExpandParent?: boolean;
  disabled?: boolean;
  showLine?: boolean;
  checkStrictly?: boolean;
  defaultExpandedKeys?: string[];
  defaultCheckedKeys?: string[];
  defaultSelectedKeys?: string[];
  onLoadMore?: (nodeType: string, key: string) => Promise<any>;
  onSelection?: Function,
  onChecked?: Function,
  onSelected?: Function,
  onExpanded?: (info: any) => void;
  getGroupTitleForPath?: Function;
  cachedItems?: CachedData;
};

const base64Data = 'PHN2ZyB3aWR0aD0iMjBweCIgaGVpZ2h0PSIyMHB4IiB2aWV3Qm94PSIwIDAgMzIgMzIiIGlkPSJMYXllcl8xIiBkYXRhLW5hbWU9IkxheWVyIDEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3QgZmlsbD0iIzAwOTdBRiIgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIi8+PHBvbHlnb24gZmlsbD0id2hpdGUiIHBvaW50cz0iMTMgMjQgNCAxNSA1LjQxNCAxMy41ODYgMTMgMjEuMTcxIDI2LjU4NiA3LjU4NiAyOCA5IDEzIDI0Ii8+PC9zdmc+';

const useStyles = makeStyles(() => ({
  treeRoot : {
    '& .rc-tree-checkbox-checked' : {
      backgroundImage : `url("data:image/svg+xml;base64,${dlsSvgColorize(base64Data, dlsColor('aaBlue'))}") !important`
    },
    '& .rc-tree-node-selected' : {
      backgroundColor : `${dlsColor('lightBlue')} !important`
    }
  }
}));

const switcherIcon = (obj) => {
  const {data, expanded} = obj;
  if (data.isAsset) {
    return <AssetVehicleIcon size={16} />;
  }
  if (data.isDevice) {
    return <ScDeviceIcon size={16} />;
  }
  if (!data.isAsset && !data.isDevice && !data.children?.length) {
    return null;
  }
  if (expanded) {
    return <MaterialExpandLessIcon size={18} />;
  }
  return <MaterialExpandMoreIcon size={18} />;
};

/**
 * Component to render tree view. Use key prop to force rerender.
 *
 * @param {React.Component} nodes The component contains tree nodes.
 *
 * ```typescript
 *   import {SelectionTree} from '@ion/dls/src/selectiontree';
 *
 *   <SelectionTree nodes={treeNodes ?? []}
 *     key={`${defaultSelectedKeys}`} />
 * ```
 */
export const SelectionTree = ({
  nodes,
  className,
  cachedItems,
  onChecked,
  onExpanded,
  onSelected,
  getGroupTitleForPath,
  autoExpandParent = true,
  disabled = false,
  showLine = true,
  checkStrictly = false,
  defaultExpandedKeys = [],
  defaultCheckedKeys = [],
  defaultSelectedKeys = []
}: TreeDisplayProps) => {
  const classes = useStyles();

  useEffect(function() {
    const scrollIntoView = async() => {
      const selectedKeyTitle = await getGroupTitleForPath(
        defaultSelectedKeys[0],
        cachedItems);
      document.querySelector(`[title="${selectedKeyTitle}"]`)?.scrollIntoView();
    };

    if (defaultSelectedKeys) {
      scrollIntoView();
    }
  }, [defaultSelectedKeys]);

  const onCheck = useCallback((checkedKeys, info) => {
    onChecked?.(checkedKeys);
  }, [onChecked]);

  const onExpand = useCallback((expandedKeys, info) => {
    onExpanded?.(info);
  }, [onExpanded]);

  const onSelect = useCallback((selectedKeys, info) => {
    onSelected?.(info);
  }, [onSelected]);

  return (
    <Tree className={`${classnames('selection-tree', className, classes.treeRoot)}`}
      defaultExpandedKeys={defaultExpandedKeys}
      defaultCheckedKeys={defaultCheckedKeys}
      defaultSelectedKeys={defaultSelectedKeys}
      onCheck={onCheck}
      onExpand={onExpand}
      onSelect={onSelect}
      selectedKeys={defaultSelectedKeys}
      autoExpandParent={autoExpandParent}
      disabled={disabled}
      showLine={showLine}
      checkStrictly={checkStrictly}
      switcherIcon={switcherIcon}
      showIcon={false}
      checkable
      multiple>
      {nodes ?? []}
    </Tree>
  );
};
